import { useCallback, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCheckoutFunnel } from '../../hooks/useCheckoutFunnel';
import { useTranslation } from '@shared/core';
import { useCheckoutInitDataContext } from '@apps/registry/guest/components/CheckoutDialog/hooks/useCheckoutInitData';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useCheckoutDialogContext } from '../../CheckoutDialog.context';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestSessionIdentity } from '@shared/hooks/useGuestSessionIdentity';
import { useSeedFieldOnce } from '@shared/hooks/useSeedFieldOnce';
import { useCheckoutTelemetryData } from '@apps/registry/guest/components/CheckoutDialog/hooks';
import { addAction } from '@shared/utils/logger';

type GifterDetailsFormFields = {
  email: string;
  name: string;
  note: string;
};

/**
 * TODO: Handle email authenticated sessions
 */
export const useGifterDetailsController = () => {
  const {
    formValues,
    submitGuestDetails,
    context: { flowVariant, isAffiliate, isGroupGifting },
  } = useCheckoutFunnel(({ formValues, submitGuestDetails, context }) => [formValues, submitGuestDetails, context.flowVariant, context.isAffiliate, context.isGroupGifting]);
  const { registryItem } = useCheckoutInitDataContext('gifterDetails');
  const { profile, mySessionEmail } = useCheckoutDialogContext();
  const { eventHandle } = useEventInfo();

  const { addGifterDetails, reserveAndPurchase, authenticatedSession, affiliateAddGifterDetails } = useGuestRegistryTelemetry();
  const { t, tq } = useTranslation('guestRegistry');
  const [useDefaultEmail, setUseDefaultEmail] = useState<boolean>(!formValues.email && !!profile?.email);

  const translations = t('checkoutDialog', 'screens', 'gifterDetails');
  const reserveItemTranslation = t('alreadyReserveSection');
  const { fieldEmailMissing } = translations;

  // "From" rather than "Name": a gift can come from a couple or a family, and the old
  // label plus its "First and last name" placeholder told guests otherwise. All three
  // strings move together — a "From" field that rejects you for missing a "name" is
  // worse than the original.
  const fromFieldTranslations = {
    label: tq`From`,
    placeholder: tq`e.g. Evan & Julia, or The Sanchez Family`,
    missing: tq`Let the couple know who this gift is from`,
  };
  const isCashFundType = registryItem?.donationFund?.fundType === 'cash';
  const isDeferred = flowVariant === 'deferred';
  // So a guest we already know never re-types their own name and email. Skipped only once
  // the checkout carries both — a guest who gave a name but no email still needs the read.
  const { fullName: sessionName, email: sessionEmail } = useGuestSessionIdentity({
    eventHandle,
    skip: Boolean(formValues.name && formValues.email),
  });

  const userEmail = formValues.email || mySessionEmail || profile?.email || sessionEmail || '';

  const valueInMinorUnits = registryItem.productData.price?.valueInMinorUnits;

  const showCheckMailDialog = (emailId: string) => {
    authenticatedSession(emailId, 'AuthenticatedSessionChangePrompt');
    setUseDefaultEmail(false);
  };

  const { eventId, registryItemId, reservedQty, priceValueInMinorUnits, typeOfItem, productId, priceCurrencyCode, destinationUrl, productTitle } = useCheckoutTelemetryData();

  const {
    errors,
    touched,
    values,
    setFieldValue,
    getFieldProps: _getFieldProps,
    handleSubmit,
  } = useFormik<GifterDetailsFormFields>({
    initialValues: {
      email: userEmail,
      // Precedence: a name already entered in this checkout session, then the identified
      // guest's name. The session read is async, so `sessionName` is usually empty on the
      // first render and the effect below fills it in — formik snapshots `initialValues`
      // once and `enableReinitialize` would discard whatever the guest had typed.
      name: formValues.name || sessionName,
      note: formValues.note || '',
    },
    validationSchema: Yup.object<GifterDetailsFormFields>({
      email: Yup.string().email().required(fieldEmailMissing()),
      name: Yup.string().required(fromFieldTranslations.missing),
      note: Yup.string(),
    }),
    onSubmit: values => {
      // Read the submitted values, not the funnel context — `formValues` is only merged
      // by the machine on SUBMIT_GUEST_DETAILS below, so it is still stale here.
      const { email, name, note } = values;
      const { amount } = formValues;

      const telemetryData = {
        eventId,
        registryItemId,
        productId,
        gifterEmail: email,
        gifterName: name,
        gifterNote: note,
        buttonLabel: translations.cta(),
        productTitle,
      };
      if (isAffiliate) {
        const affiliateAddGifterDetailsData = {
          ...telemetryData,
          typeOfItem: 'product',
          externalUrl: registryItem?.productData.externalUrl,
          giftAmount: (formValues.quantity || 1) * (valueInMinorUnits || 0),
          giftQuantity: formValues.quantity,
        };
        affiliateAddGifterDetails(affiliateAddGifterDetailsData);
        addAction('affiliateAddGifterDetails', affiliateAddGifterDetailsData);
      } else {
        if (isDeferred) {
          const reserveAndPurchaseData = {
            ...telemetryData,
            reservedQty,
            priceValueInMinorUnits,
            priceCurrencyCode,
            destinationUrl,
            typeOfItem,
            isGroupGiftingEnabled: isGroupGifting,
          };
          reserveAndPurchase(reserveAndPurchaseData);
          addAction('reserveAndPurchase', reserveAndPurchaseData);
        } else {
          const addGifterDetailsData = {
            ...telemetryData,
            giftAmount: amount,
            donationFundId: registryItem.donationFund?.id,
            externalUrl: registryItem.productData.externalUrl,
            flowVariant,
            isGroupGiftingEnabled: isGroupGifting,
          };
          addGifterDetails(addGifterDetailsData);
          addAction('addGifterDetails', addGifterDetailsData);
        }
      }

      submitGuestDetails(values);
    },
  });

  // The identity read resolves after formik has already snapshotted `initialValues`, so
  // the fields are seeded here instead — once each, and only if we get there before the
  // guest does. See `useSeedFieldOnce` for why "empty and untouched" is not a sufficient
  // guard on its own.
  //
  // Email needs this as much as name does. The synchronous sources above (`mySessionEmail`,
  // `profile.email`) cover a guest who has transacted on this registry or is logged in, but
  // a guest identified only by their event session has neither, and their address arrives
  // with the same async read as their name.
  const seedName = useCallback((value: string) => setFieldValue('name', value), [setFieldValue]);
  const seedEmail = useCallback((value: string) => setFieldValue('email', value), [setFieldValue]);
  useSeedFieldOnce({ seedValue: sessionName, currentValue: values.name, isTouched: touched.name, onSeed: seedName });
  useSeedFieldOnce({ seedValue: sessionEmail, currentValue: values.email, isTouched: touched.email, onSeed: seedEmail });

  const getFieldProps = useCallback(
    (field: keyof GifterDetailsFormFields) => {
      return _getFieldProps(field);
    },
    [_getFieldProps],
  );

  return {
    errors: {
      email: touched.email && errors.email ? errors.email : undefined,
      name: touched.name && errors.name ? errors.name : undefined,
      note: touched.note && errors.note ? errors.note : undefined,
    },
    getFieldProps,
    handleSubmit,
    isDeferred,
    isCashFundType,
    translations,
    fromFieldTranslations,
    reserveItemTranslation,
    useDefaultEmail,
    userEmail,
    showCheckMailDialog,
    isAffiliate,
    formValues,
  };
};
